// Front-end feature flags for the merged app (Voye send-products landing in the
// LakayCart portal). These gate NEW, additive surface so it stays invisible in
// production until deliberately switched on — the zero-downtime discipline of the
// merge: nothing that touches a live customer flow ships enabled by default.
//
// Read from Vite env at build time. A flag is ON only when its var is exactly
// the string 'true'; anything else (unset, 'false', '0') is OFF. Set per
// environment in the deploy config — never here.

/**
 * Airtime top-up in the customer portal. The backend endpoints are already live
 * (airtime_enabled=true server-side); this flag controls only whether the WEB
 * portal exposes the UI + route + nav item. OFF in production until the airtime
 * customer surface is signed off and rolled out staging-first.
 */
export function airtimeEnabled(): boolean {
  return import.meta.env.VITE_AIRTIME_ENABLED === 'true';
}

/**
 * URL of the Voye mobile app (`voye-app`), for the "View on Phone" hand-off in
 * the portal. Set `VITE_MOBILE_APP_URL` per environment; when it is unset we
 * derive a dev default from the CURRENT hostname on the mobile dev port, so a
 * tester browsing the portal over the LAN gets a LAN-reachable phone URL rather
 * than a `localhost` one their phone cannot resolve.
 */
export function mobileAppUrl(): string {
  const configured = import.meta.env.VITE_MOBILE_APP_URL;
  if (typeof configured === 'string' && configured.trim() !== '') {
    return configured.trim().replace(/\/+$/, '');
  }

  if (typeof window === 'undefined') return '';

  // Only fall back to the dev port on an actual dev host. Previously this
  // applied everywhere, so a production build with the variable unset pointed
  // at https://app.lakaycart.com:5173/voye — a port nothing listens on, and a
  // button that silently went nowhere.
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || /^192\.168\./.test(host);

  if (isLocal) {
    return `${window.location.protocol}//${host}:5173/voye`;
  }

  // No sensible guess for a real host: return empty so the button hides
  // rather than offering a dead link.
  return '';
}

/**
 * The "Try a demo account" panel on the sign-in page.
 *
 * OFF unless explicitly enabled. That panel lists four addresses INCLUDING the
 * platform admin, with the shared password printed beside them — on a public
 * login page it is a map of how to get in, and it documents the password
 * convention even after the accounts themselves are disabled.
 *
 * Demo accounts only exist locally now (the production ones were purged or
 * suspended), so on production the panel advertises logins that cannot work.
 * Set VITE_DEMO_LOGINS=true for local dev and staging.
 */
export function demoLoginsEnabled(): boolean {
  return import.meta.env.VITE_DEMO_LOGINS === 'true';
}
